import React, { useState, useRef } from 'react';
import {
  ShieldCheckIcon,
  ComputerDesktopIcon,
  AcademicCapIcon,
  CodeBracketIcon,
  ChartBarIcon,
  DocumentTextIcon,
  LockClosedIcon,
  CloudArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const Services = () => {
  const [current, setCurrent] = useState(0);
  const startX = useRef(0);
  const isDragging = useRef(false);

  const services = [
    {
      icon: ShieldCheckIcon,
      title: 'Data Protection Assessment',
      description:
        'Solutions to address organizational Data privacy and protection regulation challenges.',
      details:
        'We conduct Data Impact Assessment (DIA) as the first step in implementing a comprehensive Data protection program.',
    },
    {
      icon: ComputerDesktopIcon,
      title: 'System Audits',
      description:
        'Comprehensive system audits and security assessments.',
      details:
        'Detailed evaluation of your IT infrastructure to identify vulnerabilities and compliance gaps.',
    },
    {
      icon: AcademicCapIcon,
      title: 'Capacity Building',
      description:
        'Specialized training programs for individuals and organizations.',
      details:
        'Tailored training to improve and retain skills, knowledge, and resources needed for competent and effective performance.',
    },
    {
      icon: CodeBracketIcon,
      title: 'Penetration Testing',
      description:
        'Network and Application Penetration Testing.',
      details:
        'Simulating real-world attacks to identify security weaknesses in your network and applications.',
    },
    {
      icon: ChartBarIcon,
      title: 'Vulnerability Assessments',
      description:
        'Comprehensive security assessment on critical assets.',
      details:
        'Evaluate security posture and identify vulnerabilities for adequate remediation.',
    },
    {
      icon: DocumentTextIcon,
      title: 'GAP Analysis & Risk Assessment',
      description:
        'Compare actual performance with potential performance.',
      details:
        'Identify performance gaps and implement necessary steps for improvement.',
    },
    {
      icon: LockClosedIcon,
      title: 'ISO Certification',
      description:
        'Guidance for various ISO standards certification.',
      details:
        'Expert assistance with ISO27001, ISO22301, ISO9001, ISO20000 certifications.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Web Portal Development',
      description:
        'Custom web application development.',
      details:
        'Develop portals to improve internal processes and meet specific client needs.',
    },
  ];

  const prev = () =>
    setCurrent((prev) => (prev === 0 ? services.length - 1 : prev - 1));

  const next = () =>
    setCurrent((prev) => (prev === services.length - 1 ? 0 : prev + 1));

  /* ---------------- Swipe Support ---------------- */

  const handleStart = (clientX) => {
    isDragging.current = true;
    startX.current = clientX;
  };

  const handleEnd = (clientX) => {
    if (!isDragging.current) return;
    const diff = startX.current - clientX;

    if (diff > 50) next();
    if (diff < -50) prev();

    isDragging.current = false;
  };

  /* ------------------------------------------------ */

  return (
    <section
      id="services"
      className="py-24 bg-gray-900 overflow-hidden relative"
    >
      {/* Side fade gradients */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-900 to-transparent z-20"></div>
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-900 to-transparent z-20"></div>

      <div className="container mx-auto px-4">

        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold text-[#fff8f3] mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Comprehensive IT consulting and security solutions tailored to your business needs
          </p>
          <div className="w-24 h-1 bg-red-500 mx-auto mt-6"></div>
        </div>

        <div className="relative flex items-center justify-center">

          {/* LEFT BUTTON */}
          <button
            onClick={prev}
            className="absolute left-6 md:left-16 z-30 bg-red-500 hover:bg-red-600 text-[#fff8f3] p-3 rounded-full"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          {/* STACKED CARDS */}
          <div
            className="relative w-full max-w-6xl h-[500px] flex items-center justify-center"
            onMouseDown={(e) => handleStart(e.clientX)}
            onMouseUp={(e) => handleEnd(e.clientX)}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            onTouchEnd={(e) =>
              handleEnd(e.changedTouches[0].clientX)
            }
          >
            {services.map((service, index) => {
              const position =
                (index - current + services.length) %
                services.length;

              let translateX = 0;
              let scale = 0.8;
              let opacity = 0;
              let zIndex = 0;
              let filter = 'grayscale(100%) brightness(60%)';

              if (position === 0) {
                translateX = 0;
                scale = 1;
                opacity = 1;
                zIndex = 30;
                filter = 'none';
              } else if (position === 1) {
                translateX = 220;
                scale = 0.9;
                opacity = 1;
                zIndex = 20;
              } else if (position === services.length - 1) {
                translateX = -220;
                scale = 0.9;
                opacity = 1;
                zIndex = 20;
              } else if (position === 2) {
                translateX = 360;
                scale = 0.85;
                opacity = 0.6;
                zIndex = 10;
              } else if (position === services.length - 2) {
                translateX = -360;
                scale = 0.85;
                opacity = 0.6;
                zIndex = 10;
              }

              return (
                <div
                  key={index}
                  onClick={() => setCurrent(index)}
                  className="absolute cursor-pointer transition-all duration-500 ease-in-out"
                  style={{
                    transform: `translateX(${translateX}px) scale(${scale})`,
                    opacity,
                    zIndex,
                    filter,
                  }}
                >
                  <div className="w-96 h-[480px] bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 p-8">
                    <service.icon className="h-14 w-14 text-blue-400 mb-6" />

                    <h3 className="text-2xl font-semibold text-[#fff8f3] mb-4">
                      {service.title}
                    </h3>

                    <p className="text-gray-400 mb-4">
                      {service.description}
                    </p>

                    <p className="text-sm text-gray-500">
                      {service.details}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT BUTTON */}
          <button
            onClick={next}
            className="absolute right-6 md:right-16 z-30 bg-red-500 hover:bg-red-600 text-[#fff8f3] p-3 rounded-full"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mt-12">
          {services.map((_, index) => (
            <div
              key={index}
              onClick={() => setCurrent(index)}
              className={`w-3 h-3 rounded-full cursor-pointer transition ${
                index === current
                  ? 'bg-blue-500'
                  : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;